// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import loadVariantChildren from "@insite/client-framework/Store/Data/Products/Handlers/LoadVariantChildren";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import { Checkbox, DataTableCell, DataTableRow, Hidden, Icon, Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import BomTableProductChild from "@stoneage/App/Components/ProductDetails/BomTableProductChild";
import ProductQuantity, { ProductQuantityStyles } from "@stoneage/App/Components/Qty/ProductQuantity";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import ProductContextAvailability from "@stoneage/Overrides/Components/ProductContextAvailability";
import ProductPrice, { ProductPriceStyles } from "@stoneage/Overrides/Components/ProductPrice";
import React, { useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const isAuthenticated =
        (state.context.session.isAuthenticated || state.context.session.rememberMe) && !state.context.session.isGuest;
    return {
        showPricing: isAuthenticated && state.context.showPricing,
        sortOrders: state.xProductBom[ownProps?.mainProductNumber]?.sortOrders,
        bundles: state.xProductBom[ownProps?.mainProductNumber]?.bundles,
        checkedProducts: state.xProductBomAction.checkedProducts,
        isAuthenticated,
        variantChildren: ownProps.product?.isVariantParent
            ? getVariantChildrenDataView(state, ownProps.product.id).value!
            : null,
        productTempQuantity:
            state.xProductBom[ownProps?.mainProductNumber]?.productsTempQuantities?.[
                ownProps?.productInfo?.productId || ""
            ] || 0,
        sessionCurrency: state.context.session.currency,
    };
};

const mapDispatchToProps = {
    loadVariants: makeHandlerChainAwaitable(loadVariantChildren),
};

interface OwnProps {
    product: ProductModel;
    productInfo: ProductInfo | undefined;
    mainProductNumber: string;
    description?: string;
    productNumber?: string;
    displaySequence?: boolean;
    extendedStyles?: BomTableProductCardStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BomTableProductCardStyles {
    cellStyle?: DataTableCellProps;
    costStyles?: ProductPriceStyles;
    checkBox?: CheckboxPresentationProps;
    productQuantityOrderedStyles?: TextFieldProps;
    qtyOrderedField?: ProductQuantityStyles;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    expandIconWrapper?: InjectableCss;
    configureText?: TypographyPresentationProps;
    icon?: IconPresentationProps;
    headingRow?: DataTableRowProps;
    childrenHeaderCell?: DataTableCellProps;
    row?: DataTableRowProps;
    descriptionCellStyle?: DataTableCellProps;
    partNumberCellStyle?: DataTableCellProps;
    sortOrderCellStyle?: DataTableCellProps;
}

export const bomTableProductCardStyles: BomTableProductCardStyles = {};

const BomTableProductCard = ({
    product,
    extendedStyles,
    productInfo,
    showPricing,
    sortOrders,
    checkedProducts,
    isAuthenticated,
    description,
    productNumber,
    loadVariants,
    variantChildren,
    mainProductNumber,
    bundles,
    productTempQuantity,
    displaySequence = true,
    sessionCurrency,
}: Props) => {
    const [styles] = useState(() => mergeToNew(bomTableProductCardStyles, extendedStyles));
    const [qty, setQty] = useState(productInfo?.qtyOrdered || 1);
    const [unitOfMeasure, setUnitOfMeasure] = useState(productInfo?.unitOfMeasure || "");
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);

    const dispatch = useDispatch();

    const toggleExpand = useCallback(() => {
        setIsExpanded(prevIsExpanded => !prevIsExpanded);
    }, [setIsExpanded]);

    useEffect(() => {
        if (isExpanded && !variantChildren && !bundles?.[productNumber || ""]) {
            setIsDataLoading(true);
            loadVariants({ productId: product.id });
        }
    }, [isExpanded]);

    useEffect(() => {
        if (variantChildren) {
            setIsDataLoading(false);
        }
    }, [variantChildren]);

    useEffect(() => {
        if (productInfo && productInfo?.productId && Object.keys(checkedProducts).includes(productInfo.productId)) {
            dispatch({
                type: "xProductBomAction/updateProduct",
                productInfo: { ...productInfo, qtyOrdered: qty, unitOfMeasure },
            });
        }
    }, [qty, unitOfMeasure]);

    useEffect(() => {
        productInfo?.unitOfMeasure && setUnitOfMeasure(productInfo.unitOfMeasure);
    }, [productInfo?.unitOfMeasure]);

    useEffect(() => {
        if (productInfo && productTempQuantity) {
            updateQuantity(productTempQuantity);
        }
    }, [productTempQuantity]);

    if ((!product || !productInfo) && !description) {
        return null;
    }
    if (!product || !productInfo) {
        return (
            <DataTableRow key={productNumber}>
                <DataTableCell data-th={translate("#")} {...styles.sortOrderCellStyle}>
                    {productNumber && displaySequence && sortOrders?.[productNumber]}
                </DataTableCell>
                <DataTableCell translate="no" data-th={translate("Part ID")} {...styles.partNumberCellStyle}>
                    {productNumber}
                </DataTableCell>
                <DataTableCell data-th={translate("Description")} {...styles.descriptionCellStyle}>
                    {description}
                </DataTableCell>
                <DataTableCell data-th={translate("Inventory")}></DataTableCell>
                <DataTableCell data-th={translate("MSRP")} {...styles.cellStyle}></DataTableCell>
                <DataTableCell data-th={translate("Disc. %")} {...styles.cellStyle}></DataTableCell>
                <DataTableCell data-th={translate("Cost")} {...styles.cellStyle}></DataTableCell>
                <DataTableCell data-th={translate("QTY")} {...styles.cellStyle}></DataTableCell>
                <DataTableCell data-th={" "} {...styles.cellStyle}></DataTableCell>
            </DataTableRow>
        );
    }

    const updateQuantity = (qtyOrdered: number) => {
        setQty(qtyOrdered);
        // call dispatch if quantity has changed
        if (productTempQuantity !== qtyOrdered) {
            dispatch({
                type: "xProductBom/UpdateQuantity",
                productId: productInfo.productId,
                quantity: qtyOrdered,
                key: mainProductNumber,
            } as any);
        }
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        setUnitOfMeasure(unitOfMeasure);
    };

    const productContext = {
        product: {
            ...product,
        },
        productInfo: {
            ...productInfo,
            qtyOrdered: qty,
            unitOfMeasure,
        },
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    } as ProductContextModel;

    const handleChanged = (_: React.SyntheticEvent) => {
        dispatch({
            type: "xProductBomAction/updateSelection",
            productInfo: productContext.productInfo,
            product: productContext.product,
        });
    };

    return (
        <>
            <ProductContext.Provider key={product.id} value={productContext}>
                <DataTableRow {...styles.row} key={product.id} id={product.id}>
                    <DataTableCell data-th={translate("#")} {...styles.sortOrderCellStyle}>
                        {displaySequence && sortOrders?.[product.productNumber]}
                    </DataTableCell>
                    <DataTableCell translate="no" data-th={translate("Part ID")} {...styles.partNumberCellStyle}>
                        {product.canonicalUrl && product.canonicalUrl.length !== 0 ? (
                            <Link translate="no" href={product.canonicalUrl}>
                                {product.productNumber}
                            </Link>
                        ) : (
                            product.productNumber
                        )}
                    </DataTableCell>
                    <DataTableCell data-th={translate("Description")} {...styles.descriptionCellStyle}>
                        {product.productTitle}
                    </DataTableCell>
                    <DataTableCell data-th={translate("Inventory")}>
                        {isAuthenticated && !product.isVariantParent && (
                            <StyledWrapper>
                                <ProductContextAvailability isProductDetailsPage={false} />
                            </StyledWrapper>
                        )}
                    </DataTableCell>
                    <DataTableCell translate="no" data-th={translate("MSRP")} {...styles.cellStyle}>
                        {showPricing && !product.isVariantParent && (
                            <>
                                {!productContext.productInfo.pricing ||
                                productContext.productInfo.pricing.unitListPrice === 0 ? (
                                    "––.––"
                                ) : (
                                    <> {productContext.productInfo.pricing?.unitListPriceDisplay}</>
                                )}
                            </>
                        )}
                    </DataTableCell>
                    <DataTableCell translate="no" data-th={translate("Disc. %")} {...styles.cellStyle}>
                        {showPricing && !product.isVariantParent && (
                            <>
                                {!productContext.productInfo.pricing ? (
                                    "-%"
                                ) : (
                                    <ProductPrice
                                        product={productContext}
                                        showInvalidPriceMessage={false}
                                        showLabel={false}
                                        showUnitOfMeasure={false}
                                        showPack={false}
                                        showOnlySavingPercent={true}
                                    />
                                )}
                            </>
                        )}
                    </DataTableCell>
                    <DataTableCell translate="no" data-th={translate("Cost")} {...styles.cellStyle}>
                        {showPricing && !product.isVariantParent && (
                            <>
                                {!productContext.productInfo.pricing ||
                                productContext.productInfo.pricing.unitNetPrice === 0 ? (
                                    <Typography translate="no">
                                        {sessionCurrency?.currencySymbol || sessionCurrency?.currencyCode}–.––
                                    </Typography>
                                ) : (
                                    <Typography translate="no">
                                        {productContext.productInfo.pricing?.unitNetPriceDisplay}
                                    </Typography>
                                )}
                            </>
                        )}
                    </DataTableCell>
                    <DataTableCell data-th={translate("QTY")} {...styles.cellStyle}>
                        {!product.isVariantParent && (
                            <ProductQuantity
                                value={qty}
                                showLabel={false}
                                extendedStyles={styles.qtyOrderedField}
                                allowCantBuy
                            />
                        )}
                    </DataTableCell>
                    {product.isVariantParent ? (
                        <DataTableCell data-th={" "} {...styles.cellStyle}>
                            <Hidden id={`${product.id}_click`} onClick={() => setIsExpanded(true)}></Hidden>
                            <StyledWrapper {...styles.expandIconWrapper} onClick={toggleExpand}>
                                <Typography {...styles.configureText}>{translate("Configure")}</Typography>
                                <Icon
                                    {...styles.icon}
                                    size={10}
                                    src={isExpanded ? ChevronUp : ChevronDown}
                                    color="common.black"
                                />
                            </StyledWrapper>
                        </DataTableCell>
                    ) : (
                        <DataTableCell data-th=" " {...styles.cellStyle}>
                            <Hidden above="md">
                                <Checkbox
                                    {...styles.checkBox}
                                    checked={!!checkedProducts[product.id]}
                                    onChange={handleChanged}
                                    data-test-selector="productCarousel_SelectCheckbox"
                                >
                                    {translate("Add to Quote")}
                                </Checkbox>
                            </Hidden>
                            <Hidden below="lg">
                                <Checkbox
                                    {...styles.checkBox}
                                    checked={!!checkedProducts[product.id]}
                                    onChange={handleChanged}
                                    data-test-selector="productBom_SelectCheckbox"
                                />
                            </Hidden>
                        </DataTableCell>
                    )}
                </DataTableRow>
                {isDataLoading ? (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                ) : (
                    <>
                        {isExpanded && (
                            <>
                                <DataTableRow {...styles.headingRow}>
                                    <DataTableCell></DataTableCell>
                                    <DataTableCell {...styles.childrenHeaderCell}>{translate("Part ID")}</DataTableCell>
                                    <DataTableCell {...styles.childrenHeaderCell}>
                                        {translate("Description")}
                                    </DataTableCell>
                                    <DataTableCell {...styles.childrenHeaderCell}>
                                        {translate("Inventory")}
                                    </DataTableCell>
                                    <DataTableCell {...styles.childrenHeaderCell}>{translate("MSRP")}</DataTableCell>
                                    <DataTableCell {...styles.childrenHeaderCell}>{translate("Disc. %")}</DataTableCell>
                                    <DataTableCell {...styles.childrenHeaderCell}>{translate("Cost")}</DataTableCell>
                                    <DataTableCell {...styles.childrenHeaderCell}>{translate("QTY")}</DataTableCell>
                                    <DataTableCell title="checkbox"></DataTableCell>
                                </DataTableRow>
                                <BomTableProductChild
                                    childrenProducts={bundles?.[productNumber || ""]?.bundleProducts || variantChildren}
                                    mainProductNumber={mainProductNumber}
                                    childrenType={variantChildren ? "variants" : "bundles"}
                                />
                            </>
                        )}
                    </>
                )}
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(BomTableProductCard);
